import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { removeBackground, loadImage } from '@/utils/removeBackground';
import { Loader2, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

// Import all archetype images
import thinkerImg from '@/assets/archetype-thinker.png';
import escapistImg from '@/assets/archetype-escapist.png';
import dreamerImg from '@/assets/archetype-dreamer.png';
import connectorImg from '@/assets/archetype-connector.png';
import archivistImg from '@/assets/archetype-archivist.png';
import explorerImg from '@/assets/archetype-explorer.png';

const archetypeImages = [
  { name: 'Explorer', src: explorerImg, filename: 'archetype-explorer.png' },
  { name: 'Thinker', src: thinkerImg, filename: 'archetype-thinker.png' },
  { name: 'Dreamer', src: dreamerImg, filename: 'archetype-dreamer.png' },
  { name: 'Connector', src: connectorImg, filename: 'archetype-connector.png' },
  { name: 'Escapist', src: escapistImg, filename: 'archetype-escapist.png' },
  { name: 'Archivist', src: archivistImg, filename: 'archetype-archivist.png' },
];

export default function BackgroundRemovalTool() {
  const [processing, setProcessing] = useState<string | null>(null);
  const [processedImages, setProcessedImages] = useState<Record<string, string>>({});
  const { toast } = useToast();

  const processImage = async (name: string, src: string, filename: string) => {
    setProcessing(name);
    try {
      // Fetch the image as a blob
      const response = await fetch(src);
      const blob = await response.blob();
      
      // Load image
      const img = await loadImage(blob);
      
      // Remove background with improved algorithm
      const processedBlob = await removeBackground(img);
      
      // Create object URL for preview
      const url = URL.createObjectURL(processedBlob);
      setProcessedImages(prev => ({ ...prev, [name]: url }));
      
      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      
      toast({
        title: "Success",
        description: `${name} processed successfully. Check your downloads folder.`,
      });
    } catch (error) {
      console.error(`Error processing ${name}:`, error);
      toast({
        title: "Error",
        description: `Failed to process ${name}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
    } finally {
      setProcessing(null);
    }
  };

  const processAll = async () => {
    toast({
      title: "Processing Started",
      description: "Processing all archetype images. This may take a few minutes...",
    });
    
    for (const { name, src, filename } of archetypeImages) {
      await processImage(name, src, filename);
    }
    
    toast({
      title: "All Done!",
      description: "All archetype images have been processed. Check your downloads folder.",
    });
  };

  return (
    <div className="container mx-auto p-8">
      <h1 className="text-3xl font-bold mb-6">Archetype Background Removal Tool</h1>
      
      <Card className="p-6 mb-6">
        <h2 className="text-xl font-semibold mb-3">Instructions</h2>
        <ol className="list-decimal list-inside space-y-2 mb-4 text-muted-foreground">
          <li>Click "Process All Images" to remove backgrounds from all archetype mascots</li>
          <li>The first run will download the AI model (30-60 seconds), then process each image</li>
          <li>Processed images will download automatically to your Downloads folder</li>
          <li>Replace the files in <code className="text-xs bg-muted px-1 py-0.5 rounded">src/assets/</code> with the downloaded versions</li>
          <li>The checkerboard pattern will be completely removed</li>
        </ol>
        <div className="flex gap-2">
          <Button onClick={processAll} disabled={processing !== null}>
            {processing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing {processing}...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Process All Images
              </>
            )}
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
        {archetypeImages.map(({ name, src, filename }) => (
          <Card key={name} className="p-4">
            <h3 className="font-semibold mb-2">{name}</h3>
            <div className="space-y-2">
              {/* Before */}
              <div>
                <p className="text-xs text-muted-foreground mb-1">Before:</p>
                <div className="bg-muted rounded-lg p-4">
                  <img src={src} alt={`${name} original`} className="w-full h-auto" />
                </div>
              </div>
              
              {/* After (if processed) */}
              {processedImages[name] && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">After:</p>
                  <div className="bg-gradient-to-br from-purple-100 to-blue-100 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-4">
                    <img src={processedImages[name]} alt={`${name} processed`} className="w-full h-auto" />
                  </div>
                </div>
              )}
            </div>
            
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => processImage(name, src, filename)}
              disabled={processing !== null}
              className="w-full mt-3"
            >
              {processing === name ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Process
                </>
              )}
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
}
